import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session, User } from "@supabase/supabase-js";

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      setLoading(false);

      // Send welcome email once per user (idempotency key prevents duplicates)
      if (event === "SIGNED_IN" && newSession?.user) {
        const u = newSession.user;
        const created = u.created_at ? new Date(u.created_at).getTime() : 0;
        const isNew = Date.now() - created < 1000 * 60 * 60 * 24; // within 24h
        if (isNew && u.email) {
          const flagKey = `glowie_welcome_sent_${u.id}`;
          if (!localStorage.getItem(flagKey)) {
            localStorage.setItem(flagKey, "1");
            const name =
              (u.user_metadata?.display_name as string | undefined) ||
              u.email?.split("@")[0];
            // Fire and forget; idempotency key on the server prevents duplicates
            setTimeout(() => {
              supabase.functions
                .invoke("send-transactional-email", {
                  body: {
                    templateName: "welcome",
                    recipientEmail: u.email,
                    idempotencyKey: `welcome-${u.id}`,
                    templateData: { name },
                  },
                })
                .catch(() => {
                  // Allow retry on a future sign-in if it failed
                  localStorage.removeItem(flagKey);
                });
            }, 0);
          }
        }
      }
    });

    supabase.auth.getSession().then(({ data: { session: existing } }) => {
      setSession(existing);
      setUser(existing?.user ?? null);
      setLoading(false);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};